import { sql } from "drizzle-orm";
import { pgTable, text, varchar, integer, boolean, timestamp, jsonb, real, index, unique } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import type { z } from "zod";

import { tenants } from './common';

export const inductions = pgTable("inductions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  code: text("code").notNull(),
  name: text("name").notNull(),
  type: text("type").notNull().default("general"),
  status: text("status").notNull().default("draft"),
  description: text("description"),

  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  regionScope: text("region_scope"),
  projectScope: text("project_scope"),
  siteScope: text("site_scope"),
  areaScope: text("area_scope"),
  companyTypeScope: text("company_type_scope"),
  workerCategoryScope: text("worker_category_scope"),
  roleScope: text("role_scope"),

  mandatory: boolean("mandatory").notNull().default(false),
  active: boolean("active").notNull().default(true),
  version: integer("version").notNull().default(1),
  validityPeriodDays: integer("validity_period_days"),
  expiryRequired: boolean("expiry_required").notNull().default(false),
  refresherRequired: boolean("refresher_required").notNull().default(false),
  gracePeriodDays: integer("grace_period_days").default(0),
  approvalRequired: boolean("approval_required").notNull().default(false),
  assessmentRequired: boolean("assessment_required").notNull().default(false),
  deliveryMode: text("delivery_mode").default("in-person"),

  format: text("format").default("presentation"),
  languageOptions: text("language_options").array().default([]),
  durationMinutes: integer("duration_minutes"),
  passScore: integer("pass_score"),
  attemptLimit: integer("attempt_limit"),
  contentReferences: text("content_references").array().default([]),
  acknowledgementText: text("acknowledgement_text"),

  projectRelevant: boolean("project_relevant").notNull().default(false),
  siteRelevant: boolean("site_relevant").notNull().default(false),
  areaRelevant: boolean("area_relevant").notNull().default(false),
  ptwRelevant: boolean("ptw_relevant").notNull().default(false),
  supervisorRelevant: boolean("supervisor_relevant").notNull().default(false),
  equipmentRelevant: boolean("equipment_relevant").notNull().default(false),
  emergencyRelevant: boolean("emergency_relevant").notNull().default(false),

  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  updatedBy: text("updated_by"),
});
export const insertInductionSchema = createInsertSchema(inductions).omit({ id: true, tenantId: true });
export type InsertInduction = z.infer<typeof insertInductionSchema>;
export type Induction = typeof inductions.$inferSelect;
export const inductionRecords = pgTable("induction_records", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull(),
  inductionId: text("induction_id").notNull(),
  inductionVersion: integer("induction_version").default(1),

  projectScope: text("project_scope"),
  siteScope: text("site_scope"),
  areaScope: text("area_scope"),
  companyScope: text("company_scope"),

  completionStatus: text("completion_status").notNull().default("assigned"),
  assignedDate: text("assigned_date"),
  startedDate: text("started_date"),
  completedDate: text("completed_date"),
  failedDate: text("failed_date"),
  expiredDate: text("expired_date"),
  waivedDate: text("waived_date"),

  score: integer("score"),
  trainerId: text("trainer_id"),
  trainerName: text("trainer_name"),
  attemptNumber: integer("attempt_number").default(1),

  validFrom: text("valid_from"),
  validTo: text("valid_to"),

  evidenceRefs: text("evidence_refs").array().default([]),
  acknowledgementGiven: boolean("acknowledgement_given").notNull().default(false),
  assessmentPassed: boolean("assessment_passed"),

  verified: boolean("verified").notNull().default(false),
  verifiedBy: text("verified_by"),
  approvedBy: text("approved_by"),
  waivedBy: text("waived_by"),
  waiveReason: text("waive_reason"),

  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxInductionRecordsTenantId: index("idx_induction_records_tenant_id").on(t.tenantId),
  idxInductionRecordsWorkerId: index("idx_induction_records_worker_id").on(t.workerId),
  idxInductionRecordsInductionId: index("idx_induction_records_induction_id").on(t.inductionId),
}));

// ─── Access Policies ─────────────────────────────────────────────────
export const insertInductionRecordSchema = createInsertSchema(inductionRecords).omit({ id: true, tenantId: true });
export type InsertInductionRecord = z.infer<typeof insertInductionRecordSchema>;
export type InductionRecord = typeof inductionRecords.$inferSelect;

// ─── Workforce Readiness: Training Programs ─────────────────────────
export const trainingPrograms = pgTable("training_programs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  code: text("code").notNull(),
  name: text("name").notNull(),
  type: text("type").notNull().default("general"),
  status: text("status").notNull().default("draft"),
  description: text("description"),

  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  regionScope: text("region_scope"),
  projectScope: text("project_scope"),
  siteScope: text("site_scope"),
  areaScope: text("area_scope"),
  companyTypeScope: text("company_type_scope"),
  workerCategoryScope: text("worker_category_scope"),
  roleScope: text("role_scope"),

  deliveryModel: text("delivery_model").default("internal"),
  deliveryFormat: text("delivery_format").default("classroom"),
  durationHours: real("duration_hours"),
  trainerRequired: boolean("trainer_required").notNull().default(true),
  evidenceRequired: boolean("evidence_required").notNull().default(false),
  attendanceRequired: boolean("attendance_required").notNull().default(true),
  assessmentRequired: boolean("assessment_required").notNull().default(false),
  practicalRequired: boolean("practical_required").notNull().default(false),

  validityPeriodDays: integer("validity_period_days"),
  expiryRequired: boolean("expiry_required").notNull().default(false),
  refresherIntervalDays: integer("refresher_interval_days"),
  recertificationRequired: boolean("recertification_required").notNull().default(false),
  gracePeriodDays: integer("grace_period_days").default(0),
  warningWindowDays: integer("warning_window_days").default(30),

  minAttendancePercent: integer("min_attendance_percent"),
  minScore: integer("min_score"),
  passFailRequired: boolean("pass_fail_required").notNull().default(false),
  modules: jsonb("modules").$type<{ id: string; name: string; required: boolean }[]>().default([]),
  attachments: text("attachments").array().default([]),
  practicalSignoffRequired: boolean("practical_signoff_required").notNull().default(false),
  supervisorSignoffRequired: boolean("supervisor_signoff_required").notNull().default(false),

  acceptedProviders: text("accepted_providers").array().default([]),
  trustedIssuerRequired: boolean("trusted_issuer_required").notNull().default(false),
  linkedCredentialTemplateId: text("linked_credential_template_id"),
  externalImportAllowed: boolean("external_import_allowed").notNull().default(false),
  manualOverrideAllowed: boolean("manual_override_allowed").notNull().default(false),

  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  updatedBy: text("updated_by"),
});
export const insertTrainingProgramSchema = createInsertSchema(trainingPrograms).omit({ id: true, tenantId: true });
export type InsertTrainingProgram = z.infer<typeof insertTrainingProgramSchema>;
export type TrainingProgram = typeof trainingPrograms.$inferSelect;
export const trainingRecords = pgTable("training_records", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull(),
  trainingProgramId: text("training_program_id").notNull(),
  trainingVersion: integer("training_version").default(1),

  projectScope: text("project_scope"),
  siteScope: text("site_scope"),
  areaScope: text("area_scope"),
  companyScope: text("company_scope"),

  completionStatus: text("completion_status").notNull().default("assigned"),
  assignedDate: text("assigned_date"),
  startedDate: text("started_date"),
  completedDate: text("completed_date"),
  failedDate: text("failed_date"),
  expiredDate: text("expired_date"),

  score: integer("score"),
  passFail: text("pass_fail"),
  attendancePercent: integer("attendance_percent"),
  trainerId: text("trainer_id"),
  trainerName: text("trainer_name"),
  deliveryMode: text("delivery_mode"),
  attemptNumber: integer("attempt_number").default(1),

  validFrom: text("valid_from"),
  validTo: text("valid_to"),

  attendanceSheetRef: text("attendance_sheet_ref"),
  certificateRef: text("certificate_ref"),
  assessmentResultRef: text("assessment_result_ref"),
  practicalResultRef: text("practical_result_ref"),
  trainerNotes: text("trainer_notes"),
  supervisorSignoff: text("supervisor_signoff"),
  importedSourceRef: text("imported_source_ref"),
  evidenceRefs: text("evidence_refs").array().default([]),

  verified: boolean("verified").notNull().default(false),
  verifiedBy: text("verified_by"),
  approved: boolean("approved").notNull().default(false),
  approvedBy: text("approved_by"),
  imported: boolean("imported").notNull().default(false),
  trusted: boolean("trusted").notNull().default(false),
  overridden: boolean("overridden").notNull().default(false),
  overrideReason: text("override_reason"),

  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxTrainingRecordsTenantId: index("idx_training_records_tenant_id").on(t.tenantId),
  idxTrainingRecordsWorkerId: index("idx_training_records_worker_id").on(t.workerId),
}));
export const insertTrainingRecordSchema = createInsertSchema(trainingRecords).omit({ id: true, tenantId: true });
export type InsertTrainingRecord = z.infer<typeof insertTrainingRecordSchema>;
export type TrainingRecord = typeof trainingRecords.$inferSelect;

// ─── Workforce Readiness: Competencies ──────────────────────────────
export const competencies = pgTable("competencies", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  code: text("code").notNull(),
  name: text("name").notNull(),
  type: text("type").notNull().default("technical"),
  status: text("status").notNull().default("draft"),
  description: text("description"),

  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  projectScope: text("project_scope"),
  siteScope: text("site_scope"),
  areaScope: text("area_scope"),
  roleScope: text("role_scope"),
  workTypeScope: text("work_type_scope"),
  equipmentScope: text("equipment_scope"),

  requiredInductions: text("required_inductions").array().default([]),
  requiredTraining: text("required_training").array().default([]),
  requiredCredentials: text("required_credentials").array().default([]),
  requiredExperienceMonths: integer("required_experience_months"),
  requiredAssessments: text("required_assessments").array().default([]),
  medicalFitRequired: boolean("medical_fit_required").notNull().default(false),
  supervisorApprovalRequired: boolean("supervisor_approval_required").notNull().default(false),
  externalLicenseRequired: boolean("external_license_required").notNull().default(false),

  validityPeriodDays: integer("validity_period_days"),
  reassessmentIntervalDays: integer("reassessment_interval_days"),
  expiryRequired: boolean("expiry_required").notNull().default(false),
  suspensionAllowed: boolean("suspension_allowed").notNull().default(true),
  revocationAllowed: boolean("revocation_allowed").notNull().default(true),
  renewalRules: jsonb("renewal_rules").$type<Record<string, any>>().default({}),

  theoryRequired: boolean("theory_required").notNull().default(false),
  practicalRequired: boolean("practical_required").notNull().default(false),
  observationRequired: boolean("observation_required").notNull().default(false),
  assessorRole: text("assessor_role"),
  minScore: integer("min_score"),
  evidenceRequired: boolean("evidence_required").notNull().default(false),
  dualApprovalRequired: boolean("dual_approval_required").notNull().default(false),

  ptwRelevant: boolean("ptw_relevant").notNull().default(false),
  siteAccessRelevant: boolean("site_access_relevant").notNull().default(false),
  equipmentRelevant: boolean("equipment_relevant").notNull().default(false),

  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  updatedBy: text("updated_by"),
});
export const insertCompetencySchema = createInsertSchema(competencies).omit({ id: true, tenantId: true });
export type InsertCompetency = z.infer<typeof insertCompetencySchema>;
export type Competency = typeof competencies.$inferSelect;
export const competencyAssessments = pgTable("competency_assessments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull(),
  competencyId: text("competency_id").notNull(),
  assessmentDate: text("assessment_date"),
  assessorId: text("assessor_id"),
  assessorName: text("assessor_name"),
  assessmentType: text("assessment_type").notNull().default("theory"),
  outcome: text("outcome").notNull().default("pending"),
  score: integer("score"),
  notes: text("notes"),
  evidenceRefs: text("evidence_refs").array().default([]),

  projectScope: text("project_scope"),
  siteScope: text("site_scope"),
  areaScope: text("area_scope"),

  validFrom: text("valid_from"),
  validTo: text("valid_to"),

  approvedBy: text("approved_by"),
  approvalDate: text("approval_date"),

  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxCompetencyAssessmentsTenantId: index("idx_competency_assessments_tenant_id").on(t.tenantId),
  idxCompetencyAssessmentsWorkerId: index("idx_competency_assessments_worker_id").on(t.workerId),
}));
export const insertCompetencyAssessmentSchema = createInsertSchema(competencyAssessments).omit({ id: true, tenantId: true });
export type InsertCompetencyAssessment = z.infer<typeof insertCompetencyAssessmentSchema>;
export type CompetencyAssessment = typeof competencyAssessments.$inferSelect;
export const workerCompetencyAuthorizations = pgTable("worker_competency_authorizations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull(),
  competencyId: text("competency_id").notNull(),
  sourceBasis: text("source_basis").notNull().default("assessment"),
  active: boolean("active").notNull().default(true),
  issueDate: text("issue_date"),
  expiryDate: text("expiry_date"),
  suspendedDate: text("suspended_date"),
  revokedDate: text("revoked_date"),
  restrictions: text("restrictions"),
  authorizedBy: text("authorized_by"),
  linkedAssessmentId: text("linked_assessment_id"),
  linkedCredentialId: text("linked_credential_id"),

  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxWorkerCompetencyAuthTenantId: index("idx_worker_competency_auth_tenant_id").on(t.tenantId),
  idxWorkerCompetencyAuthWorkerId: index("idx_worker_competency_auth_worker_id").on(t.workerId),
}));
export const insertWorkerCompetencyAuthorizationSchema = createInsertSchema(workerCompetencyAuthorizations).omit({ id: true, tenantId: true });
export type InsertWorkerCompetencyAuthorization = z.infer<typeof insertWorkerCompetencyAuthorizationSchema>;
export type WorkerCompetencyAuthorization = typeof workerCompetencyAuthorizations.$inferSelect;

// ─── Workforce Readiness: Rules & Bundles ───────────────────────────
export const readinessRules = pgTable("readiness_rules", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  code: text("code").notNull(),
  name: text("name").notNull(),
  status: text("status").notNull().default("draft"),
  description: text("description"),

  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  regionScope: text("region_scope"),
  projectScope: text("project_scope"),
  siteScope: text("site_scope"),
  areaScope: text("area_scope"),
  companyScope: text("company_scope"),
  workerCategoryScope: text("worker_category_scope"),
  roleScope: text("role_scope"),
  workTypeScope: text("work_type_scope"),
  equipmentScope: text("equipment_scope"),

  evaluationModel: text("evaluation_model").notNull().default("all-required"),
  requiredInductions: jsonb("required_inductions").$type<string[]>().default([]),
  requiredTraining: jsonb("required_training").$type<string[]>().default([]),
  requiredCompetencies: jsonb("required_competencies").$type<string[]>().default([]),
  requiredCredentials: jsonb("required_credentials").$type<string[]>().default([]),
  medicalClearanceRequired: boolean("medical_clearance_required").notNull().default(false),
  companyLinkRequired: boolean("company_link_required").notNull().default(false),
  projectLinkRequired: boolean("project_link_required").notNull().default(false),
  areaAssignmentRequired: boolean("area_assignment_required").notNull().default(false),
  supervisorAssignmentRequired: boolean("supervisor_assignment_required").notNull().default(false),
  ptwRoleEligibility: jsonb("ptw_role_eligibility").$type<string[]>().default([]),
  identityVerificationLevel: text("identity_verification_level"),
  // Workforce v2 Phase 7 (Task #1638): when true, the readiness evaluator emits
  // a `requirementType: "identity_document"` result based on the worker's most
  // recent active `worker_identity_documents` row (or the legacy
  // `workers.idExpiry` fallback). Replaces task #692.
  identityDocumentRequired: boolean("identity_document_required").notNull().default(false),
  documentCompleteness: jsonb("document_completeness").$type<string[]>().default([]),

  enforcementLevel: text("enforcement_level").notNull().default("hard_block"),
  overrideAllowed: boolean("override_allowed").notNull().default(false),
  escalationPath: text("escalation_path"),
  recheckIntervalDays: integer("recheck_interval_days"),
  gracePeriodDays: integer("grace_period_days").default(0),

  readinessLabel: text("readiness_label"),
  denialReasonTemplates: jsonb("denial_reason_templates").$type<string[]>().default([]),
  severity: text("severity").default("high"),
  remediationGuidance: text("remediation_guidance"),

  participationRoleScope: text("participation_role_scope").array().default([]),
  departmentScope: text("department_scope").array().default([]),

  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  updatedBy: text("updated_by"),
}, (t) => ({
  idxReadinessRulesTenantId: index("idx_readiness_rules_tenant_id").on(t.tenantId),
}));
export const insertReadinessRuleSchema = createInsertSchema(readinessRules).omit({ id: true, tenantId: true });
export type InsertReadinessRule = z.infer<typeof insertReadinessRuleSchema>;
export type ReadinessRule = typeof readinessRules.$inferSelect;
export const readinessBundles = pgTable("readiness_bundles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  code: text("code").notNull(),
  name: text("name").notNull(),
  description: text("description"),
  bundleType: text("bundle_type").notNull().default("mobilization"),
  status: text("status").notNull().default("active"),

  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  projectScope: text("project_scope"),
  siteScope: text("site_scope"),

  ruleIds: text("rule_ids").array().default([]),

  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  updatedBy: text("updated_by"),
});
export const insertReadinessBundleSchema = createInsertSchema(readinessBundles).omit({ id: true, tenantId: true });
export type InsertReadinessBundle = z.infer<typeof insertReadinessBundleSchema>;
export type ReadinessBundle = typeof readinessBundles.$inferSelect;

// ─── Workforce Readiness: Assignments, Waivers & Audit ──────────────
export const readinessAssignments = pgTable("readiness_assignments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull(),
  requirementType: text("requirement_type").notNull(),
  requirementId: text("requirement_id").notNull(),

  projectScope: text("project_scope"),
  siteScope: text("site_scope"),
  areaScope: text("area_scope"),

  assignedBy: text("assigned_by"),
  reason: text("reason"),
  dueDate: text("due_date"),
  priority: text("priority").default("normal"),
  status: text("status").notNull().default("pending"),
  notes: text("notes"),

  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxReadinessAssignmentsTenantId: index("idx_readiness_assignments_tenant_id").on(t.tenantId),
  idxReadinessAssignmentsWorkerId: index("idx_readiness_assignments_worker_id").on(t.workerId),
  idxReadinessAssignmentsStatus: index("idx_readiness_assignments_status").on(t.status),
}));
export const insertReadinessAssignmentSchema = createInsertSchema(readinessAssignments).omit({ id: true, tenantId: true });
export type InsertReadinessAssignment = z.infer<typeof insertReadinessAssignmentSchema>;
export type ReadinessAssignment = typeof readinessAssignments.$inferSelect;
export const readinessWaivers = pgTable("readiness_waivers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull(),
  waivedRequirementType: text("waived_requirement_type").notNull(),
  waivedRequirementId: text("waived_requirement_id").notNull(),

  projectScope: text("project_scope"),
  siteScope: text("site_scope"),
  areaScope: text("area_scope"),

  approvedBy: text("approved_by"),
  reason: text("reason"),
  validFrom: text("valid_from"),
  validTo: text("valid_to"),
  linkedEvidenceRefs: text("linked_evidence_refs").array().default([]),
  nonWaivable: boolean("non_waivable").notNull().default(false),
  status: text("status").notNull().default("active"),

  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertReadinessWaiverSchema = createInsertSchema(readinessWaivers).omit({ id: true, tenantId: true });
export type InsertReadinessWaiver = z.infer<typeof insertReadinessWaiverSchema>;
export type ReadinessWaiver = typeof readinessWaivers.$inferSelect;
export const readinessAuditEvents = pgTable("readiness_audit_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  eventType: text("event_type").notNull(),
  actor: text("actor"),
  actorRole: text("actor_role"),
  timestamp: text("timestamp").notNull(),
  affectedWorkerId: text("affected_worker_id"),
  affectedEntityType: text("affected_entity_type"),
  affectedEntityId: text("affected_entity_id"),
  oldState: text("old_state"),
  newState: text("new_state"),
  evidenceRefs: text("evidence_refs").array().default([]),
  comments: text("comments"),

  projectScope: text("project_scope"),
  siteScope: text("site_scope"),
  areaScope: text("area_scope"),

  ruleVersionUsed: text("rule_version_used"),

  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertReadinessAuditEventSchema = createInsertSchema(readinessAuditEvents).omit({ id: true, tenantId: true });
export type InsertReadinessAuditEvent = z.infer<typeof insertReadinessAuditEventSchema>;
export type ReadinessAuditEvent = typeof readinessAuditEvents.$inferSelect;

export const workerReadinessScores = pgTable("worker_readiness_scores", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull(),
  overallStatus: text("overall_status").notNull(),
  ruleCount: integer("rule_count").notNull().default(0),
  satisfiedCount: integer("satisfied_count").notNull().default(0),
  missingCount: integer("missing_count").notNull().default(0),
  expiredCount: integer("expired_count").notNull().default(0),
  scorePercent: real("score_percent"),
  context: jsonb("context").$type<Record<string, any>>().default({}),
  evaluatedAt: text("evaluated_at").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxWorkerReadinessScoresTenantWorker: index("idx_worker_readiness_scores_tenant_worker").on(t.tenantId, t.workerId),
  idxWorkerReadinessScoresWorkerEvaluatedAt: index("idx_worker_readiness_scores_worker_evaluated_at").on(t.workerId, t.evaluatedAt),
}));
export const insertWorkerReadinessScoreSchema = createInsertSchema(workerReadinessScores).omit({ id: true, tenantId: true });
export type InsertWorkerReadinessScore = z.infer<typeof insertWorkerReadinessScoreSchema>;
export type WorkerReadinessScore = typeof workerReadinessScores.$inferSelect;

export const workerOnboardingCases = pgTable("worker_onboarding_cases", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workerId: text("worker_id").notNull(),
  caseCode: text("case_code").notNull(),
  onboardingType: text("onboarding_type").notNull().default("new_hire"),
  status: text("status").notNull().default("draft"),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  companyId: text("company_id"),
  targetProjectId: text("target_project_id"),
  targetSiteId: text("target_site_id"),
  targetAreaId: text("target_area_id"),
  targetWorkerCategory: text("target_worker_category"),
  targetEmploymentCategory: text("target_employment_category"),
  identityDataComplete: boolean("identity_data_complete").notNull().default(false),
  personalDetailsComplete: boolean("personal_details_complete").notNull().default(false),
  emergencyContactComplete: boolean("emergency_contact_complete").notNull().default(false),
  employmentDataComplete: boolean("employment_data_complete").notNull().default(false),
  documentComplete: boolean("document_complete").notNull().default(false),
  credentialEvidenceComplete: boolean("credential_evidence_complete").notNull().default(false),
  readinessRequirementsAssigned: boolean("readiness_requirements_assigned").notNull().default(false),
  walletProvisioningState: text("wallet_provisioning_state"),
  submittedBy: text("submitted_by"),
  currentApprover: text("current_approver"),
  requiredApprovals: jsonb("required_approvals").$type<Record<string, any>>().default({}),
  checklistState: jsonb("checklist_state").$type<Record<string, any>>().default({}),
  missingItems: jsonb("missing_items").$type<string[]>().default([]),
  blockers: jsonb("blockers").$type<Record<string, any>[]>().default([]),
  dueDate: text("due_date"),
  notes: text("notes"),
  activationEligible: boolean("activation_eligible").notNull().default(false),
  activationDate: text("activation_date"),
  onboardingOutcome: text("onboarding_outcome"),
  linkedDeploymentRequestId: text("linked_deployment_request_id"),
  intakeSource: text("intake_source").notNull().default("manual"),
  bulkImportId: text("bulk_import_id"),
  createdBy: text("created_by"),
  approvedBy: text("approved_by"),
  rejectedBy: text("rejected_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertWorkerOnboardingCaseSchema = createInsertSchema(workerOnboardingCases).omit({ id: true, tenantId: true });
export type InsertWorkerOnboardingCase = z.infer<typeof insertWorkerOnboardingCaseSchema>;
export type WorkerOnboardingCase = typeof workerOnboardingCases.$inferSelect;
export const onboardingBulkImports = pgTable("onboarding_bulk_imports", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  filename: text("filename").notNull(),
  fileType: text("file_type").notNull().default("csv"),
  status: text("status").notNull().default("pending"),
  totalRows: integer("total_rows").default(0),
  successCount: integer("success_count").default(0),
  failedCount: integer("failed_count").default(0),
  skippedCount: integer("skipped_count").default(0),
  rowErrors: jsonb("row_errors").$type<Array<{ row: number; data: Record<string, unknown>; errors: string[] }>>().default([]),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertOnboardingBulkImportSchema = createInsertSchema(onboardingBulkImports).omit({ id: true, tenantId: true, createdAt: true, updatedAt: true });
export type InsertOnboardingBulkImport = z.infer<typeof insertOnboardingBulkImportSchema>;
export type OnboardingBulkImport = typeof onboardingBulkImports.$inferSelect;
export const mobilizationRequests = pgTable("mobilization_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  mobilizationCode: text("mobilization_code").notNull(),
  batchId: text("batch_id"),
  workerId: text("worker_id").notNull(),
  mobilizationType: text("mobilization_type").notNull().default("first"),
  companyId: text("company_id").notNull(),
  hostCompanyId: text("host_company_id"),
  targetProjectId: text("target_project_id").notNull(),
  targetSiteId: text("target_site_id").notNull(),
  targetAreaIds: text("target_area_ids").array().default([]),
  targetCrewTeamId: text("target_crew_team_id"),
  targetSupervisorId: text("target_supervisor_id"),
  targetRoleContext: text("target_role_context"),
  requestedMobilizationDate: text("requested_mobilization_date"),
  plannedArrivalDate: text("planned_arrival_date"),
  mobilizationWindowStart: text("mobilization_window_start"),
  mobilizationWindowEnd: text("mobilization_window_end"),
  activationEffectiveDate: text("activation_effective_date"),
  onboardingCompleteFlag: boolean("onboarding_complete_flag").notNull().default(false),
  documentsCompleteFlag: boolean("documents_complete_flag").notNull().default(false),
  inductionsCompleteFlag: boolean("inductions_complete_flag").notNull().default(false),
  trainingCompleteFlag: boolean("training_complete_flag").notNull().default(false),
  competenciesCompleteFlag: boolean("competencies_complete_flag").notNull().default(false),
  credentialsCompleteFlag: boolean("credentials_complete_flag").notNull().default(false),
  companyProjectLinkValidFlag: boolean("company_project_link_valid_flag").notNull().default(false),
  accessPolicyPrecheckFlag: boolean("access_policy_precheck_flag").notNull().default(false),
  medicalFitnessValidFlag: boolean("medical_fitness_valid_flag").notNull().default(false),
  status: text("status").notNull().default("draft"),
  currentBlockerSummary: jsonb("current_blocker_summary").$type<Record<string, any>>().default({}),
  approvers: jsonb("approvers").$type<Record<string, any>[]>().default([]),
  approvalsCompleted: jsonb("approvals_completed").$type<Record<string, any>[]>().default([]),
  comments: text("comments"),
  linkedAssignments: text("linked_assignments").array().default([]),
  createdBy: text("created_by"),
  approvedBy: text("approved_by"),
  activatedBy: text("activated_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertMobilizationRequestSchema = createInsertSchema(mobilizationRequests).omit({ id: true, tenantId: true });
export type InsertMobilizationRequest = z.infer<typeof insertMobilizationRequestSchema>;
export type MobilizationRequest = typeof mobilizationRequests.$inferSelect;
export const mobilizationBatches = pgTable("mobilization_batches", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  batchCode: text("batch_code").notNull(),
  companyId: text("company_id").notNull(),
  targetProjectId: text("target_project_id").notNull(),
  targetSiteId: text("target_site_id").notNull(),
  campaignName: text("campaign_name"),
  totalWorkers: integer("total_workers").notNull().default(0),
  readyCount: integer("ready_count").notNull().default(0),
  blockedCount: integer("blocked_count").notNull().default(0),
  pendingCount: integer("pending_count").notNull().default(0),
  status: text("status").notNull().default("draft"),
  submittedBy: text("submitted_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertMobilizationBatchSchema = createInsertSchema(mobilizationBatches).omit({ id: true, tenantId: true });
export type InsertMobilizationBatch = z.infer<typeof insertMobilizationBatchSchema>;
export type MobilizationBatch = typeof mobilizationBatches.$inferSelect;
export const workerOnboardingInvitations = pgTable("worker_onboarding_invitations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  token: text("token").notNull().unique(),
  email: text("email").notNull(),
  name: text("name").notNull(),
  phone: text("phone"),
  role: text("role").notNull().default("Worker"),
  companyId: text("company_id"),
  targetProjectId: text("target_project_id"),
  targetSiteId: text("target_site_id"),
  onboardingType: text("onboarding_type").notNull().default("new_hire"),
  status: text("status").notNull().default("pending"),
  invitedBy: text("invited_by"),
  usedAt: timestamp("used_at", { withTimezone: true }),
  usedByWorkerId: text("used_by_worker_id"),
  gdprConsentRequired: boolean("gdpr_consent_required").notNull().default(true),
  expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertWorkerOnboardingInvitationSchema = createInsertSchema(workerOnboardingInvitations).omit({ id: true, tenantId: true, createdAt: true });
export type InsertWorkerOnboardingInvitation = z.infer<typeof insertWorkerOnboardingInvitationSchema>;
export type WorkerOnboardingInvitation = typeof workerOnboardingInvitations.$inferSelect;

// ─── Onboarding Global Templates (Super Admin) ────────────────────────
export const onboardingTemplates = pgTable("onboarding_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").references(() => tenants.id),
  name: text("name").notNull(),
  description: text("description"),
  onboardingType: text("onboarding_type").notNull().default("new_hire"),
  isGlobal: boolean("is_global").notNull().default(false),
  requiredDocuments: jsonb("required_documents").$type<string[]>().default([]),
  requiredReadiness: jsonb("required_readiness").$type<{ id: string; name: string; mandatory: boolean; roleFilter?: string; companyTypeFilter?: string }[]>().default([]),
  requiredFields: jsonb("required_fields").$type<string[]>().default([]),
  gdprConsentFields: jsonb("gdpr_consent_fields").$type<string[]>().default([]),
  photoRequired: boolean("photo_required").notNull().default(false),
  signatureRequired: boolean("signature_required").notNull().default(false),
  status: text("status").notNull().default("active"),
  version: integer("version").notNull().default(1),
  publishedAt: timestamp("published_at", { withTimezone: true }),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertOnboardingTemplateSchema = createInsertSchema(onboardingTemplates).omit({ id: true, createdAt: true, updatedAt: true, version: true, publishedAt: true });
export type InsertOnboardingTemplate = z.infer<typeof insertOnboardingTemplateSchema>;
export type OnboardingTemplate = typeof onboardingTemplates.$inferSelect;

// ─── Onboarding Template Versions (immutable snapshots on publish) ─────
export const onboardingTemplateVersions = pgTable("onboarding_template_versions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  templateId: text("template_id").notNull().references(() => onboardingTemplates.id, { onDelete: "cascade" }),
  version: integer("version").notNull(),
  changelog: text("changelog"),
  snapshot: jsonb("snapshot").$type<Record<string, any>>().notNull().default({}),
  publishedAt: timestamp("published_at", { withTimezone: true }).defaultNow(),
  publishedBy: text("published_by"),
  publishedByName: text("published_by_name"),
}, (t) => ({
  uniqOnboardingTplVersion: unique("uq_onboarding_template_versions_template_version").on(t.templateId, t.version),
}));
export type OnboardingTemplateVersion = typeof onboardingTemplateVersions.$inferSelect;

// ─── Pack 13.I — New-Tenant First-Run Onboarding Flow (Task #1105) ───
//
// Persists the wizard state per tenant so the "guided onboarding modal"
// surface is fully resumable across sessions / devices, and the persistent
// "Setup checklist" sidebar item knows when to disappear (after 100% of
// the wizard steps have either been completed or explicitly skipped).
//
// One row per tenant. The row is upserted on first wizard interaction.
//
// Step keys (stable contract — also referenced by the client wizard):
//   "welcome" | "invite_team" | "branding" | "first_site"
//   | "invite_workers" | "starter_pack"
//
// `stepStates` is a map keyed by stepKey → { status, completedAt, skippedAt, payload }.
// `currentStep` records the user's last-visited step so the modal resumes
// in-place. `completed` flips when all six steps are completed-or-skipped
// and the wizard emits the `lifecycle.tenant_first_run_completed` audit
// event (one-shot — guarded by the `auditEmitted` flag).
export const tenantFirstRunStates = pgTable("tenant_first_run_states", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  currentStep: text("current_step").notNull().default("welcome"),
  stepStates: jsonb("step_states").$type<Record<string, {
    status: "pending" | "completed" | "skipped";
    completedAt?: string | null;
    skippedAt?: string | null;
    payload?: Record<string, unknown>;
  }>>().notNull().default({}),
  dismissed: boolean("dismissed").notNull().default(false),
  completed: boolean("completed").notNull().default(false),
  completedAt: timestamp("completed_at", { withTimezone: true }),
  auditEmitted: boolean("audit_emitted").notNull().default(false),
  startedAt: timestamp("started_at", { withTimezone: true }).defaultNow(),
  startedBy: text("started_by"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  updatedBy: text("updated_by"),
}, (t) => ({
  // The unique constraint on tenant_id is implemented as a unique btree
  // index by Postgres, so it doubles as the lookup index for per-tenant
  // snapshot reads — no separate idx_tenant_first_run_states_tenant_id
  // is needed. Kept named for parity with the migration constraint name.
  uniqTenantFirstRunStateTenant: unique("uq_tenant_first_run_states_tenant").on(t.tenantId),
}));
export const insertTenantFirstRunStateSchema = createInsertSchema(tenantFirstRunStates).omit({ id: true, tenantId: true, startedAt: true, updatedAt: true });
export type InsertTenantFirstRunState = z.infer<typeof insertTenantFirstRunStateSchema>;
export type TenantFirstRunState = typeof tenantFirstRunStates.$inferSelect;

// Canonical wizard step contract — kept here so server and client share the
// step-key vocabulary without drift. Adding a step REQUIRES bumping this
// list AND updating the wizard component AND the doc-register row.
export const TENANT_FIRST_RUN_STEPS = [
  "welcome",
  "invite_team",
  "branding",
  "first_site",
  "setup_permits",
  "invite_workers",
  "starter_pack",
] as const;
export type TenantFirstRunStepKey = (typeof TENANT_FIRST_RUN_STEPS)[number];

// ─── Tenant Signing Keys ──────────────────────────────────────────────
